import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../contexts/global-context';
import { Logo } from '../assets/images/logo';
import TuneIcon from '@mui/icons-material/Tune';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ForestIcon from '@mui/icons-material/Forest';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

const bannerImages = {
    // Mobile banner images (1:1)
    mobileBanner: '/banner-600x600.jpg',
    mobileSrcSet: `
        /banner-600x600.webp 600w,
        /banner-800x800.webp 800w,
        /banner-1200x1200.webp 1200w
    `,
    mobileSizes: `
        (max-width: 320px) 400px,
        (max-width: 375px) 600px,
        (max-width: 428px) 800px,
        1200px
    `,
    // Desktop banner images (21:9)
    desktopSrc: '/banner-600x257.webp',
    desktopSrcSet: `
        /banner-600x257.webp 600w,
        /banner-960x411.webp 960w,
        /banner-1920x823.webp 1920w
    `,
    desktopSizes: `
        (max-width:600px) 600px,
        (max-width:960px) 960px,
        1920px
    `
};

// Custom keyframe animations
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;


const subtleGlow = keyframes`
  0%, 100% {
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  }
  50% {
    text-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(255,255,255,0.1);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background: 'linear-gradient(105deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 30%, rgba(0,0,0,0.2) 45%, transparent 55%)';
  }
  50% {
    background: 'linear-gradient(105deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0.3) 40%, transparent 50%)';
  }
`;

export function HeroOverlay() {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [animationsStarted, setAnimationsStarted] = useState(false);
    const { isMobile } = useContext(GlobalContext);
    const theme = useTheme();
    const navigate = useNavigate();
    
    // Shared padding values for logo and text alignment
    const leftPaddingValues = { xs: '16px', sm: '20px', md: '60px' };
    
    // Handle image load
    const handleImageLoad = () => {
        setImageLoaded(true);
    };
    
    // Sequence: image loads -> fade in overlay -> start text animations
    useEffect(() => {
        if (imageLoaded && !overlayVisible) {
            // Fade in overlay after image loads
            const overlayTimer = setTimeout(() => {
                setOverlayVisible(true);
            }, 100);
            return () => clearTimeout(overlayTimer);
        }
    }, [imageLoaded, overlayVisible]);
    
    useEffect(() => {
        if (overlayVisible && !animationsStarted) {
            // Start text animations after overlay fades in
            const animationTimer = setTimeout(() => {
                setAnimationsStarted(true);
            }, 300); // Allow overlay fade to start
            return () => clearTimeout(animationTimer);
        }
    }, [overlayVisible, animationsStarted]);

    return (
        <Box sx={{ 
            position: 'relative',
            // Reserve space to prevent layout jumps - use aspect ratio containers
            width: '100%',
            aspectRatio: isMobile ? '1 / 1' : '21 / 9', // Mobile 1:1, Desktop 21:9
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            '@media (min-width: 1550px)': {
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
            },
            '@media (min-width: 1700px)': {
                borderBottomLeftRadius: '20px',
                borderBottomRightRadius: '20px',
            },
            '@media (min-width: 2000px)': {
                borderBottomLeftRadius: '24px',
                borderBottomRightRadius: '24px',
            },
            overflow: 'hidden',
            // Only show shadow after image loads to prevent flash
            boxShadow: imageLoaded ? 3 : 0,
            transition: 'box-shadow 0.5s ease-in-out',
            // Keep transparent background to blend with page
            backgroundColor: 'transparent',
        }}>
            {/* Banner Image */}
            { isMobile ? (
                <Box
                    component="img"
                    src={bannerImages.mobileBanner}
                    srcSet={bannerImages.mobileSrcSet}
                    sizes={bannerImages.mobileSizes}
                    alt="Custom Woodworking Plans"
                    onLoad={handleImageLoad}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: 'cover',
                        display: 'block',
                        opacity: imageLoaded ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out',
                    }}
                />
            ) : (
                <Box
                    component="img"
                    src={bannerImages.desktopSrc}
                    srcSet={bannerImages.desktopSrcSet}
                    sizes={bannerImages.desktopSizes}
                    alt="Custom Woodworking Plans"
                    onLoad={handleImageLoad}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: 'cover',
                        display: 'block',
                        opacity: imageLoaded ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out',
                    }}
                />
            )}
            
            {/* Angled gradient overlay with subtle animation */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isMobile 
                    ? 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.2) 85%, transparent 100%)'
                    : 'linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, rgba(0, 0, 0, 0.2) 50%, transparent 60%)',
                pointerEvents: 'none',
                zIndex: 1,
                opacity: overlayVisible ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(105deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 25%, transparent 40%)',
                    animation: overlayVisible ? `${gradientShift} 8s ease-in-out infinite` : 'none',
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                    },
                }
            }} />

            {/* Logo positioned at top-left - Desktop only */}
            {!isMobile && (
                <Box sx={{
                    position: 'absolute',
                    top: 20,
                    left: leftPaddingValues,
                    zIndex: 3,
                    opacity: 0, // Start invisible
                    animation: animationsStarted ? `${fadeIn} 0.4s ease-out 0.1s both` : 'none',
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                        opacity: animationsStarted ? 1 : 0,
                    }
                }}>
                    <Logo 
                        width={200}
                        primaryFill={theme.palette.primary.main}
                        secondaryFill={theme.palette.common.white}
                    />
                </Box>
            )}
            
            {/* Text content centered vertically */}
            <Box sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                zIndex: 2
            }}>
                <Box sx={{ 
                    maxWidth: '1920px',
                    width: '100%',
                    pl: leftPaddingValues,
                    textAlign: 'left',
                    color: 'white'
                }}>
                
                {/* Content container with restricted width */}
                <Box sx={{
                    width: { xs: '100%', md: '40%' },
                    maxWidth: { xs: '100%', md: '800px' }
                }}>
                    {/* Main headline with compact sizing for banner */}
                    <Typography 
                        variant="h1" 
                        sx={{
                            mt: { xs: 4, md: 3 },
                            fontSize: { xs: '2.5rem', md: '4.5rem' },
                            fontWeight: 800,
                            textShadow: '0 4px 8px rgba(0,0,0,0.5)',
                            opacity: 0, // Start invisible
                            animation: animationsStarted ? `${fadeIn} 0.4s ease-out 0.15s both` : 'none',
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                                opacity: animationsStarted ? 1 : 0,
                            },
                            lineHeight: { xs: 1.1, sm: 1.0 },
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {'Perfect-Fit\nWoodworking\nPlans'}
                    </Typography>

                    {/* First supporting line - stronger weight and presence */}
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                            fontWeight: 500,
                            mt: { xs: 1.5, md: 6 },
                            color: 'rgba(255,255,255,0.95)',
                            textShadow: '0 2px 6px rgba(0,0,0,0.7)',
                            opacity: 0, // Start invisible
                            animation: animationsStarted ? `${fadeIn} 0.4s ease-out 0.2s both, ${subtleGlow} 3s ease-in-out 1.5s infinite` : 'none',
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                                opacity: animationsStarted ? 1 : 0,
                            },
                            '&:hover': {
                                transform: 'translateX(5px)',
                                transition: 'transform 0.3s ease',
                                textShadow: '0 2px 8px rgba(0, 0, 0, 0.45), 0 0 25px rgba(255,255,255,0.15)',
                            }
                        }}
                    >
                        Start designing for free. Generate a build-ready plan in minutes.
                    </Typography>

                    {/* CTA Button with micro secondary line */}
                    <Box sx={{ mt: { xs: 2, md: 5 } }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/designs')}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.8rem', md: '0.95rem' },
                                px: { xs: 2.5, md: 3 },
                                py: { xs: 1, md: 1.15 },
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                boxShadow: '0 6px 18px rgba(0,0,0,0.25)', // Subtle depth shadow
                                opacity: 0, // Start invisible
                                animation: animationsStarted ? `${fadeIn} 0.3s ease-out 0.3s both` : 'none',
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                    opacity: animationsStarted ? 1 : 0,
                                },
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    transform: 'translateX(5px) scale(1.05)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                },
                                transition: 'all 0.3s ease-in-out'
                            }}
                        >
                            Start Designing
                        </Button>
                        
                        {/* Micro secondary line */}
                        <Typography
                            sx={{
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                                fontWeight: 400,
                                mt: 1,
                                color: 'rgba(255,255,255,0.95)',
                                textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                                textAlign: 'left',
                                opacity: 0, // Start invisible
                                animation: animationsStarted ? `${fadeIn} 0.3s ease-out 0.4s both` : 'none',
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                    opacity: animationsStarted ? 1 : 0,
                                }
                            }}
                        >
                            Free to customize · One-time purchase · No AI slop
                        </Typography>
                    </Box>
                </Box>
                </Box>
            </Box>

            {/* Values strip at bottom of hero */}
            <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 3,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(10px)',
                display: { xs: 'none', md: 'block' },
                opacity: overlayVisible ? 1 : 0,
                transition: 'opacity 0.6s ease-in-out 0.3s',
            }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    maxWidth: '1536px',
                    mx: 'auto',
                }}>
                    {[
                        { icon: <TuneIcon />, title: 'No CAD skills', desc: 'Easy to tune parameters.' },
                        { icon: <ViewInArIcon />, title: 'See it in 3D', desc: 'Preview every angle, AR on mobile.' },
                        { icon: <ForestIcon />, title: 'Material-smart', desc: 'Optimized cut lists, minimal waste.' },
                        { icon: <TaskAltIcon />, title: 'Build-ready', desc: 'Cut lists, dimensions, and assembly drawings.' },
                    ].map((item, i) => (
                        <Box key={i} sx={{
                            py: 2,
                            px: 3,
                            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}>
                            <Box sx={{ color: theme.palette.primary.main, fontSize: 22, display: 'flex' }}>
                                {item.icon}
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '14.5px', color: 'white', lineHeight: 1.3 }}>
                                    {item.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                                    {item.desc}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}